// services/budgetService.js - Servicio de presupuestos y órdenes de inserción
import apiClient from './api';
import { toast } from 'react-toastify';

class BudgetService {
  // Método auxiliar para manejar errores del API
  handleApiError(error, defaultMessage = 'Error en la operación') {
    if (!error.response) {
      // Error de conectividad
      toast.error('Error de conexión. Verifique su conexión a internet.', {
        theme: 'colored'
      });
      return 'Error de conexión';
    }

    const errorData = error.response.data;
    
    // Si hay múltiples errores de validación
    if (errorData.message && Array.isArray(errorData.message)) {
      const errorList = errorData.message.map((msg, index) => `${index + 1}. ${msg}`).join('\n');
      toast.error(
        <div>
          <strong>Errores encontrados:</strong>
          <br />
          <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
            {errorList}
          </div>
        </div>, 
        { 
          theme: 'colored',
          autoClose: 8000 // Más tiempo para leer múltiples errores
        }
      );
      return errorList;
    }

    // Error simple
    const message = errorData.message || defaultMessage;
    toast.error(message, { theme: 'colored' });
    return message;
  }

  // === INSERTION ORDERS ===

  // Obtener todas las órdenes de inserción
  async getAllInsertionOrders(campaignType = null) {
    try {
      // Construir URL con filtro opcional
      let url = '/budget/insertion-orders';
      if (campaignType) {
        url += `?campaignType=${campaignType}`;
      }
      
      const response = await apiClient.get(url);
      return { 
        success: true, 
        orders: response.data.data,
        campaignType: campaignType // Devolver el tipo para referencia
      };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener órdenes de inserción');
      return { success: false, error: message };
    }
  }

  // Obtener orden de inserción por ID
  async getInsertionOrderById(id) {
    try {
      const response = await apiClient.get(`/budget/insertion-orders/${id}`);
      return { success: true, order: response.data.data }; // Extraer el data
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener orden de inserción');
      return { success: false, error: message };
    }
  }

  // Crear nueva orden de inserción
  async createInsertionOrder(orderData) {
    try {
      // Formatear datos para API
      const formattedData = this.formatOrderDataForApi(orderData);

      // Validación estricta: accountId debe venir seleccionado del combo
      if (!formattedData.accountId) {
        toast.error('Debe seleccionar una cuenta válida antes de crear la orden.', { theme: 'colored' });
        return { success: false, error: 'Debe seleccionar una cuenta válida' };
      }
      
      const response = await apiClient.post('/budget/insertion-orders', formattedData);
      const result = { success: true, order: response.data.data };
      
      // Crear asignaciones para cada campaña seleccionada
      const campaignList = formattedData.campaignIds || (formattedData.campaignId ? [formattedData.campaignId] : []);
      if (formattedData.nivel === 'CAMPAIGN' && campaignList.length > 0) {
        result.assignments = [];
        for (const cid of campaignList) {
          try {
            const assignmentDataForApi = this.formatAssignmentDataForApi({
              campaignId: cid,
              accountId: formattedData.accountId,
              amount: parseFloat(formattedData.grossInvestment) || 0,
              description: `Asignación - ${formattedData.description || 'Sin descripción'}`,
              dateFrom: formattedData.dateFrom,
              dateTo: formattedData.dateTo,
              orderId: result.order.id,
            });
            const r = await this.createAssignment(assignmentDataForApi);
            if (r.success) result.assignments.push(r.assignment);
          } catch (_) { /* no falla la orden */ }
        }
      }
      
      return result;
    } catch (error) {
      const message = this.handleApiError(error, 'Error al crear orden de inserción');
      return { success: false, error: message };
    }
  }

  // Actualizar orden de inserción
  async updateInsertionOrder(id, orderData) {
    try {
      // *** FORMATEAR DATOS PARA API ***
      const formattedData = this.formatOrderDataForApi(orderData);

      // Validación estricta: accountId debe venir seleccionado del combo
      if (!formattedData.accountId) {
        toast.error('Debe seleccionar una cuenta válida antes de actualizar la orden.', { theme: 'colored' });
        return { success: false, error: 'Debe seleccionar una cuenta válida' };
      }
      
      // Actualizar la orden principal
      const response = await apiClient.put(`/budget/insertion-orders/${id}`, formattedData);
      const result = { success: true, order: response.data.data };
      
      // *** NUEVO: Si es nivel Campañas y hay campaignId, actualizar/crear asignación ***
      if (formattedData.nivel === 'CAMPAIGN' && formattedData.campaignId) {
        try {
          // Obtener asignaciones existentes
          const assignmentsResult = await this.getAssignmentsByOrderId(id);
          
          if (assignmentsResult.success && assignmentsResult.assignments.length > 0) {
            // Actualizar la primera asignación existente
            const firstAssignment = assignmentsResult.assignments[0];
            
            const updateData = {
              campaignId: formattedData.campaignId,
              accountId: formattedData.accountId,
              description: `Asignación actualizada - ${formattedData.description || 'Sin descripción'}`,
              dateFrom: formattedData.dateFrom,
              dateTo: formattedData.dateTo
              // Mantener amount y otros campos existentes
            };
            
            const updateResult = await this.updateAssignment(firstAssignment.id, updateData);
            
            if (updateResult.success) {
              result.updatedAssignment = updateResult.assignment;
            }
          } else {
            // No hay asignaciones, crear una nueva
            const assignmentData = {
              campaignId: formattedData.campaignId,
              accountId: formattedData.accountId,
              amount: parseFloat(formattedData.grossInvestment) || 0,
              description: `Asignación inicial para orden - ${formattedData.description || 'Sin descripción'}`,
              dateFrom: formattedData.dateFrom,
              dateTo: formattedData.dateTo
            };
            
            const assignmentDataForApi = this.formatAssignmentDataForApi({
              ...assignmentData,
              orderId: id
            });
            
            const createResult = await this.createAssignment(assignmentDataForApi);
            
            if (createResult.success) {
              result.newAssignment = createResult.assignment;
            }
          }
        } catch (assignmentError) {
          // No falla la actualización de la orden, solo log del errordw
        }
      }
      
      return result;
    } catch (error) {
      const message = this.handleApiError(error, 'Error al actualizar orden de inserción');
      return { success: false, error: message };
    }
  }

  // Eliminar orden de inserción
  async deleteInsertionOrder(id) {
    try {
      const response = await apiClient.delete(`/budget/insertion-orders/${id}`);
      return { success: true, message: response.data.message || 'Eliminado exitosamente' };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al eliminar orden de inserción');
      return { success: false, error: message };
    }
  }

  // === MÉTODOS AUXILIARES ===

  // Validar datos de orden de inserción
  validateInsertionOrder(orderData) {
    const errors = [];

    if (!orderData.campaign_name || orderData.campaign_name.trim() === '') {
      errors.push('El nombre de la campaña es requerido');
    }

    if (!orderData.insertion_order_name || orderData.insertion_order_name.trim() === '') {
      errors.push('El nombre de la orden de inserción es requerido');
    }

    if (!orderData.budget || orderData.budget <= 0) {
      errors.push('El presupuesto debe ser mayor a 0');
    }

    if (!orderData.start_date) {
      errors.push('La fecha de inicio es requerida');
    }

    if (!orderData.end_date) {
      errors.push('La fecha de fin es requerida');
    }

    if (orderData.start_date && orderData.end_date) {
      const startDate = new Date(orderData.start_date);
      const endDate = new Date(orderData.end_date);
      
      if (startDate >= endDate) {
        errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Formatear datos para API (CREAR ORDEN)
  formatOrderDataForApi(orderData) {

    // Helper para normalizar IDs que pueden venir como 'undefined'/'null' o vacíos
    const normalizeId = (value) => {
      if (value === null || value === undefined) return null;
      const str = String(value).trim();
      if (str === '' || str.toLowerCase() === 'undefined' || str.toLowerCase() === 'null') return null;
      return str;
    };

    // *** MAPEO DE BUSINESS UNIT A ID NUMÉRICO ***
    const businessUnitMapping = {
      'SOCIAL': 1,
      'SEARCH': 2,
      'DISPLAY': 3,
      'VIDEO': 4
    };
    
    // *** MAPEAR NIVEL CORRECTAMENTE ***
    let nivel = 'CAMPAIGN'; // Default
    if (orderData.nivelAsignacion === 'Cuenta') {
      nivel = 'ACCOUNT';
    } else if (orderData.nivelAsignacion === 'Campañas') {
      nivel = 'CAMPAIGN';
    }

    // *** DETERMINAR ACCOUNT ID Y CAMPAIGN ID SEGÚN EL NIVEL - CORREGIDO ***
    let accountId = null;
    let campaignId = null;
    
    const accountIdRaw = normalizeId(orderData.accountId);
    const selectedAccountForCampaignsRaw = normalizeId(orderData.selectedAccountForCampaigns);
    const cuentaIdRaw = normalizeId(orderData.cuentaId);
    const campaignIdRaw = normalizeId(orderData.campaignId);

    if (nivel === 'ACCOUNT' && accountIdRaw) {
      // Nivel cuenta: usar accountId directamente
      accountId = accountIdRaw;
    } else if (nivel === 'CAMPAIGN') {
      // Nivel campaña: usar accountId si existe; si no, usar selectedAccountForCampaigns
      if (accountIdRaw) {
        accountId = accountIdRaw;
      } else if (selectedAccountForCampaignsRaw) {
        accountId = selectedAccountForCampaignsRaw;
      } else if (cuentaIdRaw) {
        accountId = cuentaIdRaw;
      }
      
      // Asignar campaignId si existe
      if (campaignIdRaw) {
        campaignId = campaignIdRaw;
      }
    } else if (cuentaIdRaw) {
      // Fallback al sistema antiguo
      accountId = cuentaIdRaw;
    }

    // *** OBTENER NOMBRE DEL CLIENTE ***
    // CORREGIDO: Usar siempre clienteName del estado. Eliminado el mapeo estático.
    const clientName = orderData.clienteName || `Cliente ${orderData.clienteId || 'Desconocido'}`;

    // *** MANEJAR FECHAS PARA EVITAR NULLS ***
    let dateFrom = this.combineDateAndTime(orderData.fechaDesde, orderData.horaDesde);
    let dateTo = this.combineDateAndTime(orderData.fechaHasta, orderData.horaHasta);

    if (!dateFrom || !dateTo) {
      // Fallback por si acaso, aunque no debería ocurrir con la nueva lógica
      const now = new Date();
      if (!dateFrom) {
        dateFrom = this.combineDateAndTime(new Date(now.getFullYear(), now.getMonth(), now.getDate()), '00:00');
      }
      if (!dateTo) {
        dateTo = this.combineDateAndTime(new Date(now.getFullYear() + 1, 11, 31), '23:59');
      }
    }

    const formattedData = {
      clientName: clientName,
      clientId: parseInt(orderData.clienteId) || 0,
      businessUnit: orderData.businessUnit,
      businessUnitId: businessUnitMapping[orderData.businessUnit] || 1,
      grossInvestment: parseFloat(orderData.inversionBruta) || 0,
      netInvestment: parseFloat(orderData.inversionNeta) || 0,
      brokerFee: orderData.brokerFee ? parseFloat(orderData.brokerFee) : null,
      mediaFee: orderData.mediaFee ? parseFloat(orderData.mediaFee) : null,
      adminFee: orderData.feeAdmin ? parseFloat(orderData.feeAdmin) : null,
      dateFrom, // Usar fecha procesada (hora local)
      dateTo,   // Usar fecha procesada (hora local)
      currency: orderData.moneda || 'USD',
      currencyId: parseInt(orderData.currencyId) || 1,
      description: orderData.descripcion || 'Sin descripción',
      tablaMedio: orderData.businessUnit || 'SOCIAL',
      nivel: nivel,
      accountId: accountId || undefined,
      campaignId: campaignId || undefined,
      campaignIds: Array.isArray(orderData.campaignIds) && orderData.campaignIds.length > 0
        ? orderData.campaignIds.filter(id => id && id !== 'undefined' && id !== 'null')
        : (campaignId ? [campaignId] : undefined),
      funnelStage: orderData.funnelStage || null,
    };

    return formattedData;
  }

  // Función auxiliar para combinar fecha y hora
  combineDateAndTime(dateString, timeString) {
    try {
      if (!dateString) return null;
      
      // Normalizar a objeto Date sin mutar el original
      let baseDate;
      if (dateString instanceof Date) {
        baseDate = new Date(dateString.getTime());
      } else {
        baseDate = new Date(dateString);
      }
      if (isNaN(baseDate.getTime())) return null;
      
      // Aplicar hora si se provee
      if (timeString) {
        const [hours, minutes] = String(timeString).split(':');
        baseDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
      }

      // Devolver string en hora local sin zona (evita corrimiento por UTC)
      const yyyy = baseDate.getFullYear();
      const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
      const dd = String(baseDate.getDate()).padStart(2, '0');
      const HH = String(baseDate.getHours()).padStart(2, '0');
      const MM = String(baseDate.getMinutes()).padStart(2, '0');
      const SS = '00';
      return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`;
    } catch (error) {
      console.error('Error combinando fecha y hora:', error);
      return null;
    }
  }

  // Calcular estadísticas de presupuesto
  calculateBudgetStats(orders) {
    if (!Array.isArray(orders) || orders.length === 0) {
      return {
        totalBudget: 0,
        totalSpent: 0,
        remainingBudget: 0,
        spentPercentage: 0,
        ordersCount: 0
      };
    }

    const totalBudget = orders.reduce((sum, order) => sum + (order.budget || 0), 0);
    const totalSpent = orders.reduce((sum, order) => sum + (order.spent || 0), 0);
    const remainingBudget = totalBudget - totalSpent;
    const spentPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

    return {
      totalBudget,
      totalSpent,
      remainingBudget,
      spentPercentage: Math.round(spentPercentage * 100) / 100, // Redondear a 2 decimales
      ordersCount: orders.length
    };
  }

  // =====================================================
  // ASSIGNMENT METHODS
  // =====================================================

  // Obtener asignaciones por orden ID
  async getAssignmentsByOrderId(orderId) {
    try {
      const response = await apiClient.get(`/budget/insertion-orders/${orderId}/assignments`);
      
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return {
          success: true,
          assignments: response.data.data
        };
      }
      
      return {
        success: false,
        error: 'Formato de datos inválido recibido del servidor'
      };
    } catch (error) {
      console.error('Error fetching assignments:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al cargar las asignaciones'
      };
    }
  }

  // Crear nueva asignación
  async createAssignment(assignmentData) {
    try {
      const response = await apiClient.post('/budget/assignments', assignmentData);
      
      if (response.data && response.data.data) {
        return {
          success: true,
          assignment: response.data.data
        };
      }
      
      return {
        success: false,
        error: 'No se recibió confirmación del servidor'
      };
    } catch (error) {
      console.error('Error creating assignment:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al crear la asignación'
      };
    }
  }

  // Actualizar asignación existente
  async updateAssignment(assignmentId, assignmentData) {
    try {
      const response = await apiClient.put(`/budget/assignments/${assignmentId}`, assignmentData);
      
      if (response.data && response.data.data) {
        return {
          success: true,
          assignment: response.data.data
        };
      }
      
      return {
        success: false,
        error: 'No se recibió confirmación del servidor'
      };
    } catch (error) {
      console.error('Error updating assignment:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al actualizar la asignación'
      };
    }
  }

  // Eliminar asignación
  async deleteAssignment(assignmentId) {
    try {
      await apiClient.delete(`/budget/assignments/${assignmentId}`);
      return {
        success: true,
        message: 'Asignación eliminada exitosamente'
      };
    } catch (error) {
      console.error('Error deleting assignment:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al eliminar la asignación'
      };
    }
  }

  // Validar exceso de presupuesto
  async validateBudgetExcess(orderId, assignmentData) {
    try {
      const response = await apiClient.post(`/budget/insertion-orders/${orderId}/validate-budget`, assignmentData);
      
      return {
        success: true,
        validation: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error validating budget:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al validar el presupuesto'
      };
    }
  }

  // Formatear datos de asignación para API (CREAR)
  formatAssignmentDataForApi(formData) {
    // *** CORRECCIÓN CRÍTICA: Usar IDs reales como strings válidos según el contexto de la orden ***
    let campaignId = null; // Fallback por seguridad - usar null cuando no hay ID válido

    // Determinar el ID correcto según el nivel de la orden y los datos disponibles
    if (formData.campaignId && formData.campaignId !== '' && formData.campaignId !== 'DEFAULT_CAMPAIGN') {
      // Si tenemos campaignId específico (nivel CAMPAIGN), usarlo
      campaignId = String(formData.campaignId);
    } else if (formData.selectedAccountForCampaigns && formData.selectedAccountForCampaigns !== '' && formData.selectedAccountForCampaigns !== 'DEFAULT_CAMPAIGN') {
      // Si tenemos selectedAccountForCampaigns (nivel CAMPAIGN), usarlo
      campaignId = String(formData.selectedAccountForCampaigns);
    } else if (formData.accountId && formData.accountId !== '' && formData.accountId !== 'DEFAULT_CAMPAIGN') {
      // Si tenemos accountId (nivel ACCOUNT), usarlo
      campaignId = String(formData.accountId);
    } else if (formData.cuentaId && formData.cuentaId !== '' && formData.cuentaId !== 'DEFAULT_CAMPAIGN') {
      // Fallback al sistema antiguo
      campaignId = String(formData.cuentaId);
    }

    return {
      insertionOrderId: formData.orderId,
      dateFrom: this.combineDateAndTime(formData.dateFrom, null),
      dateTo: this.combineDateAndTime(formData.dateTo, null),
      description: formData.description,
      montoValor: parseFloat(formData.amount) || 0,
      amountType: formData.amountType || 'fixed',
      campaignId: campaignId, // *** USAR ID STRING VÁLIDO O NULL ***
      diasSemanaCorre: formData.daysPerWeek?.toString() || '7',
      investmentType: formData.investmentType || 'CPM',
      isActive: true
    };
  }

  // Formatear datos de asignación para API (ACTUALIZAR - sin insertionOrderId)
  formatAssignmentDataForUpdate(formData) {
    // *** CORRECCIÓN CRÍTICA: Usar IDs reales como strings válidos según el contexto de la orden ***
    let campaignId = null; // Fallback por seguridad - usar null cuando no hay ID válido

    // Determinar el ID correcto según el nivel de la orden y los datos disponibles
    if (formData.campaignId && formData.campaignId !== '' && formData.campaignId !== 'DEFAULT_CAMPAIGN') {
      // Si tenemos campaignId específico (nivel CAMPAIGN), usarlo
      campaignId = String(formData.campaignId);
    } else if (formData.selectedAccountForCampaigns && formData.selectedAccountForCampaigns !== '' && formData.selectedAccountForCampaigns !== 'DEFAULT_CAMPAIGN') {
      // Si tenemos selectedAccountForCampaigns (nivel CAMPAIGN), usarlo
      campaignId = String(formData.selectedAccountForCampaigns);
    } else if (formData.accountId && formData.accountId !== '' && formData.accountId !== 'DEFAULT_CAMPAIGN') {
      // Si tenemos accountId (nivel ACCOUNT), usarlo
      campaignId = String(formData.accountId);
    } else if (formData.cuentaId && formData.cuentaId !== '' && formData.cuentaId !== 'DEFAULT_CAMPAIGN') {
      // Fallback al sistema antiguo
      campaignId = String(formData.cuentaId);
    }

    return {
      dateFrom: this.combineDateAndTime(formData.dateFrom, null),
      dateTo: this.combineDateAndTime(formData.dateTo, null),
      description: formData.description,
      montoValor: parseFloat(formData.amount) || 0,
      amountType: formData.amountType || 'fixed',
      campaignId: campaignId, // *** USAR ID STRING VÁLIDO O NULL ***
      diasSemanaCorre: formData.daysPerWeek?.toString() || '7',
      investmentType: formData.investmentType || 'CPM',
      isActive: true
    };
  }

  // *** NUEVO: Obtener gastos reales desde la tabla de hechos ***
  
  /**
   * Obtener gastos reales de todas las órdenes activas
   */
  async getAllActualSpent() {
    try {
      const response = await apiClient.get('/budget/actual-spent');
      return {
        success: true,
        actualSpentData: response.data.data || [],
        message: response.data.message
      };
    } catch (error) {
      console.error('Error getting actual spent data:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener gastos reales',
        actualSpentData: []
      };
    }
  }

  /**
   * Obtener gasto real de una orden específica
   */
  async getActualSpentByOrderId(orderId) {
    try {
      const response = await apiClient.get(`/budget/actual-spent/${orderId}`);
      return {
        success: true,
        actualSpent: response.data.data?.actualSpent || 0,
        message: response.data.message
      };
    } catch (error) {
      console.error(`Error getting actual spent for order ${orderId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener gasto real',
        actualSpent: 0
      };
    }
  }

  // ================================
  // MÉTODOS PARA OBTENER CAMPAÑAS Y CUENTAS REALES
  // ================================

  /**
   * Obtener campañas disponibles por cliente
   * @param {number} clientId - ID del cliente
   * @returns {Promise<Object>} Resultado con campañas disponibles
   */
  async getCampaignsByClient(clientId) {
    try {

      
      const response = await apiClient.get(`/budget/campaigns/${clientId}`);
      
      
      
      // La estructura es: response.data.data.campaigns
      const campaigns = response.data.data?.campaigns || [];
      
      
      
      return {
        success: true,
        campaigns: Array.isArray(campaigns) ? campaigns : [],
        message: response.data.data?.message || 'Campañas obtenidas exitosamente'
      };
    } catch (error) {
      console.error('❌ budgetService.getCampaignsByClient - Error:', error);
      return {
        success: false,
        campaigns: [],
        message: `Error: ${error.response?.data?.message || error.message}`
      };
    }
  }

  /**
   * Obtener campañas por cliente + cuenta + medio (backend optimizado)
   */
  async getCampaigns({ clientId, accountId, medium, startDate, endDate }) {
    try {
      const params = new URLSearchParams();
      if (clientId) params.append('clientId', clientId);
      if (accountId) params.append('accountId', accountId);
      if (medium) params.append('medium', medium);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const url = `/budget/campaigns?${params.toString()}`;
      const response = await apiClient.get(url);
      const campaigns = response.data?.data?.campaigns || response.data?.campaigns || [];
      return { success: true, campaigns };
    } catch (error) {
      console.error('❌ getCampaigns error:', error);
      return { success: false, campaigns: [], message: error.response?.data?.message || error.message };
    }
  }

  /**
   * Obtener cuentas disponibles por cliente (desde estructura dimensional)
   * @param {number} clientId - ID del cliente
   * @returns {Promise<Object>} Resultado con cuentas disponibles
   */
  async getAccountsByClient(clientId) {
    try {

      
      const response = await apiClient.get(`/budget/accounts/${clientId}`);
      
      
      
      // La estructura es: response.data.data.accounts
      const accounts = response.data.data?.accounts || [];
      

      
      return {
        success: true,
        accounts: Array.isArray(accounts) ? accounts : [],
        message: response.data.data?.message || 'Cuentas obtenidas exitosamente'
      };
    } catch (error) {
      console.error('❌ budgetService.getAccountsByClient - Error:', error);
      return {
        success: false,
        accounts: [],
        message: `Error: ${error.response?.data?.message || error.message}`
      };
    }
  }

  /**
   * Obtener lista de clientes disponibles
   * @returns {Promise<Object>} Resultado con clientes disponibles
   */
  async getClients() {
    try {
      const response = await apiClient.get('/budget/clients');
      return {
        success: true,
        clients: response.data?.data || [],
        message: response.data?.message || 'Clientes obtenidos exitosamente'
      };
    } catch (error) {
      console.error('Error obteniendo clientes:', error);
      return {
        success: false,
        clients: [],
        message: error.response?.data?.message || `Error: ${error.message}`
      };
    }
  }

  // =====================================================
  // DASHBOARD METHODS
  // =====================================================

  // Obtener resumen del dashboard de forma optimizada
  async getDashboardSummary() {
    try {
      const response = await apiClient.get('/budget/dashboard/summary');
      
      if (response.data && response.data.data) {
        return {
          success: true,
          orders: response.data.data.orders || [],
          assignments: response.data.data.assignments || [],
          metrics: response.data.data.metrics || {}
        };
      }
      
      return {
        success: false,
        error: 'Formato de datos inválido recibido del servidor'
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al cargar el resumen del dashboard'
      };
    }
  }

  /**
   * Obtener métricas generales para el dashboard ejecutivo
   * Incluye KPIs de Meta/Facebook, tendencias y distribución por cliente
   */
  async getGeneralDashboardMetrics(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.clientId) queryParams.append('clientId', filters.clientId);

      const url = `/budget/dashboard/general-metrics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          meta: response.data.meta
        };
      }
      
      return {
        success: false,
        error: 'Error al obtener las métricas del dashboard'
      };
    } catch (error) {
      console.error('Error fetching general dashboard metrics:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al cargar las métricas del dashboard'
      };
    }
  }

  /**
   * Obtener métricas detalladas por campaña para dashboard de cliente (estilo Looker Studio)
   */
  async getCampaignDashboardMetrics(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.clientId) queryParams.append('clientId', filters.clientId);
      if (filters.accountId) queryParams.append('accountId', filters.accountId);
      if (filters.campaignId) queryParams.append('campaignId', filters.campaignId);
      if (filters.platform) queryParams.append('platform', filters.platform);
      if (filters.productType) queryParams.append('productType', filters.productType);
      if (filters.campaignObjective) queryParams.append('campaignObjective', filters.campaignObjective);
      if (filters.modelo) queryParams.append('modelo', filters.modelo);
      if (filters.light) queryParams.append('light', 'true');

      const url = `/budget/dashboard/campaign-metrics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          meta: response.data.meta
        };
      }
      
      return {
        success: false,
        error: 'Error al obtener las métricas de campañas'
      };
    } catch (error) {
      console.error('Error fetching campaign dashboard metrics:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al cargar las métricas de campañas'
      };
    }
  }

  /**
   * Sincronizar Illumin desde API (ayer + hoy) y reprocesar en BD
   */
  async syncIlluminData({ clientId } = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (clientId) queryParams.append('clientId', clientId);
      const response = await apiClient.post(`/budget/illumin/sync?${queryParams.toString()}`);
      if (response.data?.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        error: response.data?.message || 'Error al sincronizar Illumin',
      };
    } catch (error) {
      console.error('Error syncing Illumin:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error?.message || 'Error al sincronizar Illumin',
      };
    }
  }

  // =====================================================
  // INVESTMENT COMPLIANCE (lightweight)
  // =====================================================

  async getInvestmentCompliance({ clientId, dateFrom, dateTo } = {}) {
    try {
      const params = new URLSearchParams();
      if (clientId) params.append('clientId', clientId);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      const response = await apiClient.get(`/budget/dashboard/investment-compliance?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error en getInvestmentCompliance:', error);
      return { success: false, error: 'Error obteniendo cumplimiento de inversión' };
    }
  }

  // =====================================================
  // BUDGET CONTROL METHODS
  // =====================================================

  async getBudgetControlData(filters = {}) {
    try {
      // Construir parámetros de consulta
      const queryParams = new URLSearchParams();
      
      if (filters.dateFrom) {
        queryParams.append('dateFrom', filters.dateFrom);
      }
      
      if (filters.dateTo) {
        queryParams.append('dateTo', filters.dateTo);
      }
      
      if (filters.clientId) {
        queryParams.append('clientId', filters.clientId);
      }

      const url = `/budget/control-data${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || [],
          meta: response.data.meta || {}
        };
      }
      
      return {
        success: false,
        error: response.data?.error || 'Error al obtener los datos de control de presupuestos'
      };
    } catch (error) {
      console.error('Error en getBudgetControlData:', error);
      return {
        success: false,
        error: 'Error de conexión al obtener los datos de control de presupuestos'
      };
    }
  }

  /**
   * 🎯 Obtener datos de control de presupuestos para Performance
   * OPTIMIZADO: Usa el endpoint ultra-fast para mejor rendimiento
   */
  async getBudgetControlDataPerformance(filters = {}) {
    try {
      const response = await apiClient.get('/budget/control-data/performance/optimized');
      return response.data;
    } catch (error) {
      console.error('Error fetching budget control data (Performance):', error);
      throw error;
    }
  }

  // =====================================================
  // 🎨 BRANDING BUDGET CONTROL METHODS
  // =====================================================
  
  async getBudgetControlDataBranding(filters = {}) {
    try {
      const response = await apiClient.get('/budget/control-data/branding/optimized');
      return response.data;
    } catch (error) {
      console.error('Error fetching budget control data (Branding):', error);
      throw error;
    }
  }

  // =====================================================
  // CLARO DASHBOARD METHODS
  // =====================================================

  async getClaroDashboardMetrics(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.productType) queryParams.append('productType', filters.productType);
      if (filters.platform) queryParams.append('platform', filters.platform);
      if (filters.accountId) queryParams.append('accountId', filters.accountId);
      if (filters.campaignId) queryParams.append('campaignId', filters.campaignId);

      const url = `/budget/dashboard/claro-metrics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          meta: response.data.meta
        };
      }
      
      return {
        success: false,
        error: 'Error al obtener las métricas de Claro'
      };
    } catch (error) {
      console.error('Error fetching Claro dashboard metrics:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al cargar las métricas de Claro'
      };
    }
  }
  // =====================================================
  // CLARO JIT METHODS
  // =====================================================

  async uploadJitFile(formData) {
    try {
      const response = await apiClient.post('/claro-jit/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 2 * 60 * 1000,
      });
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message };
      }
      return { success: false, error: 'Error al cargar archivo JIT' };
    } catch (error) {
      console.error('Error uploading JIT file:', error);
      return { success: false, error: error.response?.data?.message || 'Error al cargar archivo JIT' };
    }
  }

  async getJitUploadStatus(jobId) {
    try {
      const response = await apiClient.get(`/claro-jit/upload-status/${jobId}`);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al consultar estado' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al consultar estado' };
    }
  }

  async getJitUploads() {
    try {
      const response = await apiClient.get('/claro-jit/uploads');
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener historial de cargas' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al obtener historial' };
    }
  }

  async getJitPeriods() {
    try {
      const response = await apiClient.get('/claro-jit/periods');
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener períodos' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al obtener períodos' };
    }
  }

  async getJitDashboard(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.periodoReporte) queryParams.append('periodoReporte', filters.periodoReporte);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.canalValidado) queryParams.append('canalValidado', filters.canalValidado);
      if (filters.grupoTransaccion) queryParams.append('grupoTransaccion', filters.grupoTransaccion);
      if (filters.tipoProducto) queryParams.append('tipoProducto', filters.tipoProducto);
      if (filters.productType) queryParams.append('productType', filters.productType);
      if (filters.platform) queryParams.append('platform', filters.platform);
      if (filters.campaign) queryParams.append('campaign', filters.campaign);
      if (filters.excludeInactiveCampaigns === false) {
        queryParams.append('excludeInactiveCampaigns', 'false');
      }

      const url = `/claro-jit/dashboard${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data, meta: response.data.meta };
      }
      return { success: false, error: 'Error al obtener dashboard JIT' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar dashboard JIT' };
    }
  }

  async deleteJitUpload(uploadId) {
    try {
      const response = await apiClient.delete(`/claro-jit/uploads/${uploadId}`);
      if (response.data && response.data.success) {
        return { success: true, message: response.data.message };
      }
      return { success: false, error: 'Error al eliminar upload' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al eliminar' };
    }
  }

  async getWhatsappDashboard(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.productType) queryParams.append('productType', filters.productType);
      if (filters.contactado) queryParams.append('contactado', filters.contactado);
      if (filters.respuesta) queryParams.append('respuesta', filters.respuesta);
      const url = `/claro-whatsapp/dashboard${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener dashboard WhatsApp' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar dashboard WhatsApp' };
    }
  }

  async uploadWhatsappFile(formData) {
    try {
      const response = await apiClient.post('/claro-whatsapp/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 3 * 60 * 1000,
      });
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message };
      }
      return { success: false, error: 'Error al subir archivo WhatsApp' };
    } catch (error) {
      console.error('Error uploading WhatsApp file:', error);
      return { success: false, error: error.response?.data?.message || 'Error al subir archivo' };
    }
  }

  async getWhatsappUploadStatus(jobId) {
    try {
      const response = await apiClient.get(`/claro-whatsapp/upload-status/${jobId}`);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al consultar estado' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al consultar estado' };
    }
  }

  async getJitExecutiveReport(periodoReporte) {
    try {
      const url = `/claro-jit/executive-report?periodoReporte=${periodoReporte}`;
      const response = await apiClient.get(url);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener reporte gerencial' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar reporte gerencial' };
    }
  }
  // =====================================================
  // FORD SALES METHODS
  // =====================================================

  async uploadFordSalesFile(formData, periodoReporte) {
    try {
      const response = await apiClient.post(`/ford-sales/upload?periodoReporte=${periodoReporte}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 2 * 60 * 1000,
      });
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data, message: response.data.message };
      }
      return { success: false, error: 'Error al cargar archivo de ventas Ford' };
    } catch (error) {
      console.error('Error uploading Ford sales file:', error);
      return { success: false, error: error.response?.data?.message || 'Error al cargar archivo' };
    }
  }

  async getFordSalesUploadStatus(jobId) {
    try {
      const response = await apiClient.get(`/ford-sales/upload-status/${jobId}`);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al consultar estado' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al consultar estado' };
    }
  }

  async getFordSalesUploads() {
    try {
      const response = await apiClient.get('/ford-sales/uploads');
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener historial' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al obtener historial' };
    }
  }

  async getFordSalesPeriods() {
    try {
      const response = await apiClient.get('/ford-sales/periods');
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener períodos' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al obtener períodos' };
    }
  }

  async getFordSalesDashboard(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.periodoReporte) queryParams.append('periodoReporte', filters.periodoReporte);
      if (filters.modelo) queryParams.append('modelo', filters.modelo);
      if (filters.agencia) queryParams.append('agencia', filters.agencia);
      if (filters.tipoVenta) queryParams.append('tipoVenta', filters.tipoVenta);

      const url = `/ford-sales/dashboard${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener dashboard' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar dashboard' };
    }
  }

  async deleteFordSalesUpload(uploadId) {
    try {
      const response = await apiClient.delete(`/ford-sales/uploads/${uploadId}`);
      if (response.data && response.data.success) {
        return { success: true, message: response.data.message };
      }
      return { success: false, error: 'Error al eliminar upload' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al eliminar' };
    }
  }

  async getFordSalesExecutiveReport(periodoReporte) {
    try {
      const url = `/ford-sales/executive-report?periodoReporte=${periodoReporte}`;
      const response = await apiClient.get(url);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener reporte gerencial' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar reporte' };
    }
  }

  // =====================================================
  // FORD REPORTS GENERATOR
  // =====================================================

  async getFordReportPeriods() {
    try {
      const response = await apiClient.get('/ford-reports/periods');
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false, error: 'Error al obtener períodos' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar períodos' };
    }
  }

  async getFordReportPreview(periodoDesde, periodoHasta) {
    try {
      const desde = periodoDesde || periodoHasta;
      const hasta = periodoHasta || periodoDesde;
      const params = new URLSearchParams({
        periodoDesde: desde,
        periodoHasta: hasta,
      });
      const response = await apiClient.get(
        `/ford-reports/preview?${params.toString()}`,
        { timeout: 120000 },
      );
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false, error: 'Error al obtener vista previa' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error en vista previa' };
    }
  }

  async downloadFordReport(periodoDesde, periodoHasta, type = 'all', useAi = false) {
    const desde = periodoDesde || periodoHasta;
    const hasta = periodoHasta || periodoDesde;
    const params = new URLSearchParams({
      periodoDesde: desde,
      periodoHasta: hasta,
      type,
      useAi: String(useAi),
    });
    const response = await apiClient.get(
      `/ford-reports/generate?${params.toString()}`,
      { responseType: 'blob', timeout: 300000, validateStatus: (s) => s < 500 || s === 500 },
    );
    if (response.status >= 400) {
      const text = await response.data.text();
      let message = `Error ${response.status} al generar reporte`;
      try {
        const parsed = JSON.parse(text);
        message = parsed?.error?.message || parsed?.message || message;
      } catch {
        if (text) message = text.slice(0, 300);
      }
      throw new Error(message);
    }
    return {
      blob: response.data,
      aiUsed: response.headers['x-ford-ai-used'] === 'true',
      aiWarning: response.headers['x-ford-ai-warning'] || null,
    };
  }

  async downloadFordDailyReport(dataThrough, useAi = true) {
    const params = new URLSearchParams();
    if (dataThrough) params.append('dataThrough', dataThrough);
    params.append('useAi', String(!!useAi));
    const response = await apiClient.get(
      `/ford-reports/daily?${params.toString()}`,
      { responseType: 'blob', timeout: 300000, validateStatus: (s) => s < 500 || s === 500 },
    );
    if (response.status >= 400) {
      const text = await response.data.text();
      let message = `Error ${response.status} al generar reporte diario`;
      try {
        const parsed = JSON.parse(text);
        message = parsed?.error?.message || parsed?.message || message;
      } catch {
        if (text) message = text.slice(0, 300);
      }
      throw new Error(message);
    }
    return {
      blob: response.data,
      alertCount: response.headers['x-ford-daily-alerts'] || null,
      aiUsed: response.headers['x-ford-daily-ai-used'] === 'true',
      aiWarning: response.headers['x-ford-daily-ai-warning'] || null,
      aiModel: response.headers['x-ford-daily-ai-model'] || null,
    };
  }

  async getFordDailyValidation(desde, hasta) {
    try {
      const params = new URLSearchParams({ desde, hasta });
      const response = await apiClient.get(
        `/ford-reports/daily-validation?${params.toString()}`,
        { timeout: 120000 },
      );
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false, error: 'Error al validar datos' };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Error al validar datos del DW',
      };
    }
  }

  async sendFordDailyReport(dataThrough, recipients) {
    try {
      const params = new URLSearchParams();
      if (dataThrough) params.append('dataThrough', dataThrough);
      const body =
        Array.isArray(recipients) && recipients.length > 0 ? { recipients } : {};
      const response = await apiClient.post(
        `/ford-reports/daily-send?${params.toString()}`,
        body,
        { timeout: 300000 },
      );
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false, error: 'Error al enviar el reporte' };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Error al enviar el reporte por correo',
      };
    }
  }

  async getFordDailyRecipients() {
    try {
      const response = await apiClient.get('/ford-reports/daily-recipients');
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false };
    } catch {
      return { success: false };
    }
  }

  async getFordReportCrmStatus() {
    try {
      const response = await apiClient.get('/ford-reports/crm-status');
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false };
    } catch {
      return { success: false };
    }
  }

  async getFordReportAiStatus(test = false) {
    try {
      const response = await apiClient.get('/ford-reports/ai-status', {
        params: test ? { test: 'true' } : {},
      });
      if (response.data?.success) return { success: true, data: response.data.data };
      return { success: false };
    } catch {
      return { success: false };
    }
  }
  // =====================================================
  // TIGO ATL DASHBOARD
  // =====================================================

  async getTigoATLDashboard(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.marca) params.append('marca', filters.marca);
      if (filters.lineaDeNegocio) params.append('lineaDeNegocio', filters.lineaDeNegocio);
      if (filters.medio) params.append('medio', filters.medio);
      const response = await apiClient.get(`/tigo-atl/dashboard?${params.toString()}`);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Error al obtener datos ATL' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar ATL' };
    }
  }

  // =====================================================
  // AI DASHBOARD INSIGHTS
  // =====================================================

  async getDashboardInsights({ kpis, campaigns, clientName, dateRange, clientId, dateFrom, dateTo, filters, dashboardType, summary, tvFranjas, tvCanales, radio, ooh, weekly }) {
    try {
      const response = await apiClient.post('/chat/dashboard-insights', {
        dashboardType: dashboardType || 'digital',
        kpis,
        campaigns,
        clientName,
        dateRange,
        clientId,
        dateFrom,
        dateTo,
        filters,
        summary,
        tvFranjas,
        tvCanales,
        radio,
        ooh,
        weekly,
      });
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data, usage: response.data.usage };
      }
      const payload = response.data?.data ?? response.data;
      if (payload?.signals || payload?.summary) {
        return { success: true, data: payload, usage: payload?.usage };
      }
      return { success: false, error: response.data?.error || payload?.error || 'Error al generar insights' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.response?.data?.data?.error || 'Error al conectar con IA' };
    }
  }

  async getSavedDashboardInsights({ clientId, dateFrom, dateTo }) {
    try {
      const params = new URLSearchParams({ clientId, dateFrom, dateTo });
      const response = await apiClient.get(`/chat/dashboard-insights?${params.toString()}`);
      if (response.data && response.data.success) {
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'No hay insights guardados' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al cargar insights' };
    }
  }
}

// Exportar instancia singleton
const budgetServiceInstance = new BudgetService();
export default budgetServiceInstance; 